import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, functions, storage } from "../firebase";
import "./InspectionScanPage.css";

const MAX_PHOTOS = 20;
const MAX_FILE_SIZE_MB = 8;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

const EMPTY_FORM = {
  pass: true,
  notes: "",
  serialMatch: true,
  conditionMatch: true,
  accessoriesIncluded: true,
  powersOnOrBasicFunctionCheck: true,
  packagingAcceptable: true,
  personalDataRemoved: true,
  inspectorName: "",
  inspectorStoreId: "",
};

function safeStr(v, fallback = "") {
  return typeof v === "string" ? v : fallback;
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function parseDateLike(value) {
  if (!value) return null;

  try {
    if (typeof value?.toDate === "function") return value.toDate();
    if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
    if (typeof value === "number") return new Date(value);
    if (typeof value === "string") {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
  } catch {
    return null;
  }
}

function fmtDate(value) {
  const d = parseDateLike(value);
  if (!d) return "—";

  try {
    return d.toLocaleString("en-ZA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function formatCountdown(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return "00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function humanizeSnake(v) {
  const raw = safeStr(v);
  if (!raw) return "—";
  return raw
    .split("_")
    .filter(Boolean)
    .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
    .join(" ");
}

function readFilePreview(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function buildDeviceFingerprint() {
  try {
    const parts = [
      navigator.userAgent || "",
      navigator.language || "",
      navigator.platform || "",
      Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      `dpr:${window.devicePixelRatio || 1}`,
    ];
    return parts.join("|");
  } catch {
    return "unknown-device";
  }
}

function getInspectionHeading(inspectionType) {
  if (inspectionType === "partner_dropoff_intake") return "Partner Drop-off Intake Inspection";
  if (inspectionType === "pickup_inspection") return "Pickup Inspection";
  return "Trade Verification Inspection";
}

function getInspectionSubheading(inspectionType, inspectionMode) {
  if (inspectionType === "partner_dropoff_intake" || inspectionMode === "partner_dropoff") {
    return "Confirm the dropped-off item matches the declared trade before platform intake is accepted.";
  }
  if (inspectionType === "pickup_inspection" || inspectionMode === "platform_pickup") {
    return "Use the declared trade information below as the baseline during the pickup-side inspection.";
  }
  return "Inspect the actual item physically and confirm whether it matches the declared trade information.";
}

function getPhotoRequirementText(inspectionMode) {
  if (inspectionMode === "partner_dropoff") {
    return "Partner drop-off intake requires at least 4 photos: front, back, identifying details, accessories or packaging, plus any defects.";
  }
  return "Upload clear photos of the actual item, including identifying details, condition, accessories, packaging, and any visible defects.";
}

export default function InspectionScanPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const token = useMemo(() => safeStr(searchParams.get("token")).trim(), [searchParams]);
  const tradeIdFromUrl = useMemo(() => safeStr(searchParams.get("tradeId")).trim(), [searchParams]);

  const [loading, setLoading] = useState(true);
  const [contextLoading, setContextLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [fatalError, setFatalError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [sessionError, setSessionError] = useState("");

  const [accessInfo, setAccessInfo] = useState(null);
  const [tradeDoc, setTradeDoc] = useState(null);
  const [workflowDoc, setWorkflowDoc] = useState(null);
  const [sellerPrepDoc, setSellerPrepDoc] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [uploadProgressText, setUploadProgressText] = useState("");

  const [deviceFingerprint] = useState(() => buildDeviceFingerprint());
  const [sessionId, setSessionId] = useState("");
  const [sessionStatus, setSessionStatus] = useState("idle");
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [sessionCountdownMs, setSessionCountdownMs] = useState(0);
  const [sessionResumed, setSessionResumed] = useState(false);

  const validateInspectionAccess = useMemo(
    () => httpsCallable(functions, "validateInspectionAccess"),
    []
  );
  const startInspectionScanSession = useMemo(
    () => httpsCallable(functions, "startInspectionScanSession"),
    []
  );
  const submitScanInspection = useMemo(
    () => httpsCallable(functions, "submitScanInspection"),
    []
  );

  const resolvedSessionExpiry = useMemo(() => parseDateLike(sessionExpiresAt), [sessionExpiresAt]);

  useEffect(() => {
    if (!resolvedSessionExpiry) {
      setSessionCountdownMs(0);
      return undefined;
    }

    const tick = () => {
      const remaining = resolvedSessionExpiry.getTime() - Date.now();
      setSessionCountdownMs(Math.max(0, remaining));
      if (remaining <= 0) {
        setSessionStatus((prev) => (prev === "completed" ? prev : "expired"));
      }
    };

    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [resolvedSessionExpiry]);

  const loadInspectionContext = useCallback(async (resolvedTradeId) => {
    if (!resolvedTradeId) return;

    setContextLoading(true);

    try {
      const tradeRef = doc(db, "Trades", resolvedTradeId);
      const wfRef = doc(db, "TradeWorkflows", resolvedTradeId);
      const sellerPrepRef = doc(
        db,
        "InspectionReports",
        resolvedTradeId,
        "events",
        "seller_self_check"
      );

      const [tradeSnap, wfSnap, sellerPrepSnap] = await Promise.all([
        getDoc(tradeRef),
        getDoc(wfRef),
        getDoc(sellerPrepRef),
      ]);

      setTradeDoc(tradeSnap.exists() ? tradeSnap.data() : null);
      setWorkflowDoc(wfSnap.exists() ? wfSnap.data() : null);
      setSellerPrepDoc(sellerPrepSnap.exists() ? sellerPrepSnap.data() : null);
    } catch (err) {
      console.error("Failed to load inspection context:", err);
      setFatalError(err?.message || "Unable to load inspection context.");
    } finally {
      setContextLoading(false);
    }
  }, []);

  const initializeScanSession = useCallback(
    async (resolvedAccessInfo, overrides = {}) => {
      if (!token || !resolvedAccessInfo?.tradeId) return;

      const resolvedInspectorName = safeStr(overrides.inspectorName ?? form.inspectorName).trim();
      const resolvedInspectorStoreId = safeStr(
        overrides.inspectorStoreId ?? form.inspectorStoreId
      ).trim();

      setSessionLoading(true);
      setSessionError("");

      try {
        const res = await startInspectionScanSession({
          token,
          tradeId: resolvedAccessInfo.tradeId,
          inspectorName: resolvedInspectorName || null,
          inspectorStoreId: resolvedInspectorStoreId || null,
          deviceFingerprint,
        });

        const payload = res?.data || {};

        if (!payload?.ok) {
          throw new Error(payload?.message || "Unable to start secure inspection session.");
        }

        const returnedSessionId = safeStr(payload.sessionId);
        if (!returnedSessionId) {
          throw new Error("Session started but no sessionId was returned.");
        }

        setSessionId(returnedSessionId);
        setSessionStatus("active");
        setSessionExpiresAt(payload.expiresAtIso || null);
        setSessionResumed(!!payload.resumed);
      } catch (err) {
        console.error("Failed to start inspection session:", err);
        setSessionId("");
        setSessionStatus("failed");
        setSessionExpiresAt(null);
        setSessionResumed(false);
        setSessionError(err?.message || err?.details || "Unable to start inspection session.");
      } finally {
        setSessionLoading(false);
      }
    },
    [deviceFingerprint, form.inspectorName, form.inspectorStoreId, startInspectionScanSession, token]
  );

  const loadAccess = useCallback(async () => {
    if (!token) {
      setFatalError("Missing inspection token.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setFatalError("");
    setSuccessMsg("");
    setSessionError("");

    setSessionId("");
    setSessionStatus("idle");
    setSessionExpiresAt(null);
    setSessionCountdownMs(0);
    setSessionResumed(false);

    try {
      const res = await validateInspectionAccess({
        token,
        ...(tradeIdFromUrl ? { tradeId: tradeIdFromUrl } : {}),
      });

      const payload = res?.data || {};

      if (!payload?.ok) {
        throw new Error(payload?.message || "Inspection link is invalid or expired.");
      }

      setAccessInfo(payload);
      await loadInspectionContext(payload.tradeId);

      setForm({
        ...EMPTY_FORM,
        inspectorName: "",
        inspectorStoreId: safeStr(payload?.dropoff?.storeId),
      });
    } catch (err) {
      console.error("validateInspectionAccess failed:", err);
      setFatalError(
        err?.message ||
          err?.details ||
          "This inspection link is invalid, expired, or no longer available."
      );
    } finally {
      setLoading(false);
    }
  }, [loadInspectionContext, token, tradeIdFromUrl, validateInspectionAccess]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  useEffect(() => {
    const derivedPass =
      form.serialMatch &&
      form.conditionMatch &&
      form.accessoriesIncluded &&
      form.powersOnOrBasicFunctionCheck &&
      form.packagingAcceptable &&
      form.personalDataRemoved;

    if (form.pass !== derivedPass) {
      setForm((prev) => ({ ...prev, pass: derivedPass }));
    }
  }, [
    form.serialMatch,
    form.conditionMatch,
    form.accessoriesIncluded,
    form.powersOnOrBasicFunctionCheck,
    form.packagingAcceptable,
    form.personalDataRemoved,
    form.pass,
  ]);

  const inspectionType = safeStr(accessInfo?.inspectionType);

  const inspectionMode = useMemo(() => {
    const mode = safeStr(accessInfo?.inspectionMode);

    if (mode) return mode;
    if (inspectionType === "partner_dropoff_intake") return "partner_dropoff";
    if (inspectionType === "pickup_inspection") return "platform_pickup";

    return "";
  }, [accessInfo?.inspectionMode, inspectionType]);

  const tradeId = safeStr(accessInfo?.tradeId);
  const tradeRefCode = safeStr(accessInfo?.tradeRef || accessInfo?.tradeCode || tradeId);
  const accessExpiresAt = accessInfo?.expiresAt || null;
  const partnerName = safeStr(accessInfo?.partnerName || accessInfo?.dropoff?.partnerName);
  const partnerAddress = safeStr(accessInfo?.partnerAddress || accessInfo?.dropoff?.address);
  const partnerId = safeStr(accessInfo?.dropoff?.partnerId);

  const workflowStage = safeStr(workflowDoc?.stage) || "—";

  const ALLOWED_INSPECTION_STAGES = new Set([
    "awaiting_dropoff",
    "pickup_scheduled",
  ]);

  const isStageAllowed = ALLOWED_INSPECTION_STAGES.has(workflowStage);

  const paymentStatus = safeStr(workflowDoc?.payment?.status).toLowerCase();
  const inspectionFeeStatus = safeStr(workflowDoc?.inspectionFee?.status).toLowerCase();

  const isPaymentReady = !workflowDoc?.payment || paymentStatus === "paid";
  const isInspectionFeeReady =
    inspectionMode === "partner_dropoff" ||
    !workflowDoc?.inspectionFee ||
    inspectionFeeStatus === "paid";

  const declaredTitle =
    safeStr(tradeDoc?.title) ||
    safeStr(workflowDoc?.tradeTitle) ||
    safeStr(workflowDoc?.itemTitle) ||
    safeStr(accessInfo?.itemTitle) ||
    "—";

  const declaredCategory =
    safeStr(tradeDoc?.category) ||
    safeStr(workflowDoc?.itemCategory) ||
    "—";

  const declaredDescription =
    safeStr(tradeDoc?.description) ||
    safeStr(workflowDoc?.itemDescription) ||
    "—";

  const declaredCondition =
    safeStr(sellerPrepDoc?.conditionGrade) ||
    safeStr(workflowDoc?.sellerPrep?.conditionGrade) ||
    safeStr(tradeDoc?.conditionGrade) ||
    safeStr(tradeDoc?.condition) ||
    "—";

  const declaredSerial =
    safeStr(tradeDoc?.serialOrImei) ||
    safeStr(tradeDoc?.serialNumber) ||
    safeStr(tradeDoc?.imei) ||
    safeStr(workflowDoc?.sellerPrep?.serialOrImei) ||
    "—";

  const declaredAccessories =
    Array.isArray(tradeDoc?.accessories) && tradeDoc.accessories.length
      ? tradeDoc.accessories.join(", ")
      : safeStr(tradeDoc?.accessoriesSummary) ||
        safeStr(workflowDoc?.sellerPrep?.accessoriesSummary) ||
        "—";

  const sellerDeclaredNotes =
    safeStr(sellerPrepDoc?.notes) ||
    safeStr(workflowDoc?.sellerPrep?.notes) ||
    safeStr(tradeDoc?.notes) ||
    "—";

  const sellerPrepChecklist =
    sellerPrepDoc?.checklist || workflowDoc?.sellerPrep?.checklist || null;

  const sellerPrepPhotos =
    safeArray(sellerPrepDoc?.photos).length
      ? sellerPrepDoc.photos
      : safeArray(workflowDoc?.sellerPrep?.photos);

  const listingPhotos =
    safeArray(tradeDoc?.imageURLs).length
      ? tradeDoc.imageURLs
      : safeArray(tradeDoc?.images).length
      ? tradeDoc.images
      : tradeDoc?.imageUrl
      ? [tradeDoc.imageUrl]
      : tradeDoc?.image
      ? [tradeDoc.image]
      : [];

  const isPartnerDropoff =
    inspectionMode === "partner_dropoff" || inspectionType === "partner_dropoff_intake";
  const inspectionHeading = getInspectionHeading(inspectionType);
  const inspectionSubheading = getInspectionSubheading(inspectionType, inspectionMode);

  const isSessionExpired = sessionStatus === "expired" || sessionCountdownMs <= 0;
  const hasActiveSession = !!sessionId && sessionStatus === "active" && !isSessionExpired;

  const sellerEmail = safeStr(accessInfo?.sellerEmail) || "—";

  const onToggle = (field) => {
    setForm((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const onText = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onStartOrRestartSession = async () => {
    if (!accessInfo?.tradeId) return;

    if (isPartnerDropoff && !safeStr(form.inspectorStoreId).trim()) {
      alert("Inspector store ID is required before starting a partner drop-off session.");
      return;
    }

    await initializeScanSession(accessInfo);
  };

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const currentCount = photoFiles.length;
    const remaining = MAX_PHOTOS - currentCount;

    if (remaining <= 0) {
      alert(`Maximum of ${MAX_PHOTOS} photos reached.`);
      return;
    }

    const accepted = [];
    const previews = [];

    for (const file of files.slice(0, remaining)) {
      const typeOk = ACCEPTED_TYPES.includes(file.type);
      const sizeOk = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;

      if (!typeOk) {
        alert(`${file.name} is not a supported image type.`);
        continue;
      }

      if (!sizeOk) {
        alert(`${file.name} exceeds ${MAX_FILE_SIZE_MB}MB.`);
        continue;
      }

      try {
        const preview = await readFilePreview(file);
        accepted.push(file);
        previews.push({
          id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
          name: file.name,
          url: preview,
          size: file.size,
        });
      } catch (err) {
        console.error("Preview failed:", err);
      }
    }

    setPhotoFiles((prev) => [...prev, ...accepted]);
    setPhotoPreviews((prev) => [...prev, ...previews]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removePhoto = (idx) => {
    setPhotoFiles((prev) => prev.filter((_, i) => i !== idx));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const uploadPhotos = async () => {
    if (!tradeId || !photoFiles.length) return [];

    const uploaded = [];

    for (let i = 0; i < photoFiles.length; i += 1) {
      const file = photoFiles[i];
      const ext = file.name.split(".").pop() || "jpg";
      const path = `inspectionEvidence/${tradeId}/scan/${Date.now()}_${i + 1}_${Math.random()
        .toString(36)
        .slice(2)}.${ext}`;

      const storageRef = ref(storage, path);

      setUploadProgressText(`Uploading photo ${i + 1} of ${photoFiles.length}...`);

      await uploadBytes(storageRef, file, { contentType: file.type });
      const url = await getDownloadURL(storageRef);

      uploaded.push({
        url,
        path,
        name: file.name,
        contentType: file.type,
        size: file.size,
      });
    }

    setUploadProgressText("");
    return uploaded;
  };

  const validateBeforeSubmit = () => {
    if (!token) return "Missing inspection token.";
    if (!tradeId) return "Missing trade reference.";
    if (!sessionId) return "No active scan session found. Start the session first.";
    if (!hasActiveSession) return "Your scan session is not active. Restart the session.";
    if (!isStageAllowed) return `Inspection cannot be submitted at this stage (${humanizeSnake(workflowStage)}).`;
    if (!isPaymentReady) return "Buyer payment is not yet confirmed.";
    if (!isInspectionFeeReady) return "Inspection fee is not yet confirmed.";
    if (!form.notes.trim()) return "Please enter inspection notes.";
    if (!photoFiles.length) return "Please upload at least 1 photo.";

    if (isPartnerDropoff) {
      if (!safeStr(form.inspectorStoreId).trim()) {
        return "Inspector store ID is required for partner drop-off inspection.";
      }
      if (photoFiles.length < 4) {
        return "Partner drop-off intake requires at least 4 photos.";
      }
    }

    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || successMsg) return;

    const validationError = validateBeforeSubmit();
    if (validationError) {
      alert(validationError);
      return;
    }

    setSubmitting(true);
    setFatalError("");
    setSuccessMsg("");
    setSessionError("");

    try {
      const recheck = await validateInspectionAccess({
        token,
        tradeId,
      });

      if (!recheck?.data?.ok) {
        throw new Error("Inspection access is no longer valid.");
      }

      const uploadedPhotos = await uploadPhotos();

      const payload = {
        token,
        tradeId,
        sessionId,
        deviceFingerprint,
        pass: form.pass,
        notes: form.notes.trim(),
        serialMatch: !!form.serialMatch,
        conditionMatch: !!form.conditionMatch,
        accessoriesIncluded: !!form.accessoriesIncluded,
        powersOnOrBasicFunctionCheck: !!form.powersOnOrBasicFunctionCheck,
        packagingAcceptable: !!form.packagingAcceptable,
        personalDataRemoved: !!form.personalDataRemoved,
        photos: uploadedPhotos.map((p) => p.url),
        photoMeta: uploadedPhotos,
        inspectionType: inspectionType || "pickup_inspection",
        inspectionMode: inspectionMode || "platform_pickup",
        inspectorName: safeStr(form.inspectorName).trim(),
        inspectorStoreId: safeStr(form.inspectorStoreId).trim(),
      };

      const res = await submitScanInspection(payload);
      const out = res?.data || {};

      if (!out?.ok) {
        throw new Error(out?.message || "Inspection submission failed.");
      }

      setSessionStatus("completed");
      setSuccessMsg(
        out?.message ||
          (form.pass
            ? "Inspection submitted successfully. Intake has been recorded."
            : "Inspection submitted successfully. Item failed inspection.")
      );

      setPhotoFiles([]);
      setPhotoPreviews([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("submitScanInspection failed:", err);
      const message = err?.message || err?.details || "Unable to submit inspection.";

      if (
        /session/i.test(message) ||
        /expired/i.test(message) ||
        /device/i.test(message) ||
        /token/i.test(message)
      ) {
        setSessionStatus("failed");
        setSessionError(message);
      } else {
        setFatalError(message);
      }
    } finally {
      setSubmitting(false);
      setUploadProgressText("");
    }
  };

  if (loading) {
    return (
      <div className="inspection-page">
        <div className="inspection-card inspection-card--center">
          <div className="inspection-spinner" />
          <h2>Loading inspection access...</h2>
          <p>Please wait while we validate the link.</p>
        </div>
      </div>
    );
  }

  if (fatalError && !accessInfo) {
    return (
      <div className="inspection-page">
        <div className="inspection-card inspection-card--center inspection-card--error">
          <h2>Inspection unavailable</h2>
          <p>{fatalError}</p>
          <button
            type="button"
            className="inspection-btn inspection-btn--secondary"
            onClick={() => navigate("/")}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="inspection-page">
      <div className="inspection-shell">
        <header className="inspection-header">
          <div>
            <div className="inspection-kicker">NexterZone Inspection</div>
            <h1>{inspectionHeading}</h1>
            <p className="inspection-subtitle">{inspectionSubheading}</p>
          </div>
        </header>

        <section className="inspection-card">
          <div className="inspection-meta-grid">
            <div className="inspection-meta-item">
              <span>Seller Email</span>
              <strong>{sellerEmail}</strong>
            </div>

            <div className="inspection-meta-item">
              <span>Trade Ref</span>
              <strong>{tradeRefCode || "—"}</strong>
            </div>

            <div className="inspection-meta-item">
              <span>Trade ID</span>
              <strong>{tradeId || "—"}</strong>
            </div>

            <div className="inspection-meta-item">
              <span>Inspection Type</span>
              <strong>{humanizeSnake(inspectionType)}</strong>
            </div>

            <div className="inspection-meta-item">
              <span>Inspection Mode</span>
              <strong>{humanizeSnake(inspectionMode)}</strong>
            </div>

            <div className="inspection-meta-item">
              <span>Workflow Stage</span>
              <strong>{workflowStage || "—"}</strong>
            </div>

            <div className="inspection-meta-item">
              <span>Access Expires</span>
              <strong>{fmtDate(accessExpiresAt)}</strong>
            </div>

            <div className="inspection-meta-item">
              <span>Partner</span>
              <strong>{partnerName || "—"}</strong>
            </div>

            <div className="inspection-meta-item">
              <span>Partner ID</span>
              <strong>{partnerId || "—"}</strong>
            </div>

            <div className="inspection-meta-item">
              <span>Location</span>
              <strong>{partnerAddress || "—"}</strong>
            </div>
          </div>
        </section>

        <form className="inspection-form" onSubmit={onSubmit}>
          <section className="inspection-card">
            <h2>Inspector Details</h2>
            <div className="inspection-form-grid">
              <div className="inspection-field">
                <label className="inspection-label">Inspector name</label>
                <input
                  className="inspection-input"
                  type="text"
                  value={form.inspectorName}
                  onChange={(e) => onText("inspectorName", e.target.value)}
                  placeholder="Enter inspector name"
                  disabled={submitting}
                />
              </div>

              <div className="inspection-field">
                <label className="inspection-label">
                  Inspector store ID {isPartnerDropoff ? "*" : ""}
                </label>
                <input
                  className="inspection-input"
                  type="text"
                  value={form.inspectorStoreId}
                  onChange={(e) => onText("inspectorStoreId", e.target.value)}
                  placeholder="Enter store ID"
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="inspection-submit-row" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="inspection-btn inspection-btn--secondary"
                onClick={onStartOrRestartSession}
                disabled={sessionLoading || submitting}
              >
                {sessionLoading ? "Starting Session..." : hasActiveSession ? "Restart Session" : "Start Session"}
              </button>
            </div>
          </section>

          <section
            className={`inspection-card inspection-session-card ${
              hasActiveSession
                ? "inspection-session-card--active"
                : isSessionExpired
                ? "inspection-session-card--expired"
                : "inspection-session-card--warning"
            }`}
          >
            <div className="inspection-session-card__top">
              <div>
                <h2>Secure Scan Session</h2>
                <p>Only the device that started the live session can submit this inspection.</p>
              </div>
            </div>

            <div className="inspection-session-grid">
              <div className="inspection-meta-item">
                <span>Status</span>
                <strong>
                  {hasActiveSession
                    ? sessionResumed
                      ? "Active (Resumed)"
                      : "Active"
                    : sessionStatus === "completed"
                    ? "Completed"
                    : sessionStatus === "expired"
                    ? "Expired"
                    : sessionStatus === "failed"
                    ? "Failed"
                    : sessionLoading
                    ? "Starting"
                    : "Not Active"}
                </strong>
              </div>

              <div className="inspection-meta-item">
                <span>Session ID</span>
                <strong>{sessionId || "—"}</strong>
              </div>

              <div className="inspection-meta-item">
                <span>Session Expires</span>
                <strong>{fmtDate(sessionExpiresAt)}</strong>
              </div>

              <div className="inspection-meta-item">
                <span>Time Remaining</span>
                <strong>{hasActiveSession ? formatCountdown(sessionCountdownMs) : "00:00"}</strong>
              </div>
            </div>

            {hasActiveSession ? (
              <div className="inspection-banner inspection-banner--success">
                {sessionResumed
                  ? "Existing session on this device was resumed successfully."
                  : "Secure session is active on this device."}
              </div>
            ) : null}

            {isSessionExpired ? (
              <div className="inspection-banner inspection-banner--warning">
                This session has expired. Start a new session before submitting.
              </div>
            ) : null}

            {sessionError ? (
              <div className="inspection-banner inspection-banner--error">{sessionError}</div>
            ) : null}

            {!isStageAllowed ? (
              <div className="inspection-banner inspection-banner--warning">
                Inspection cannot be submitted at this stage ({humanizeSnake(workflowStage)}).
              </div>
            ) : null}

            {(!isPaymentReady || !isInspectionFeeReady) ? (
              <div className="inspection-banner inspection-banner--warning">
                Inspection is not fully unlocked:
                {!isPaymentReady ? " Buyer payment pending." : ""}
                {!isInspectionFeeReady ? " Inspection fee pending." : ""}
              </div>
            ) : null}
          </section>

          {contextLoading ? (
            <section className="inspection-card">
              <h3>Loading trade context...</h3>
              <p>Please wait while declared trade details and seller prep evidence are loaded.</p>
            </section>
          ) : null}

          {fatalError ? (
            <section className="inspection-card inspection-card--error">
              <h3>Warning</h3>
              <p>{fatalError}</p>
            </section>
          ) : null}

          {successMsg ? (
            <section className="inspection-card inspection-card--success">
              <h3>Inspection submitted</h3>
              <p>{successMsg}</p>
            </section>
          ) : null}

          <section className="inspection-card">
            <h2>Declared Trade Details</h2>
            <div className="inspection-meta-grid">
              <div className="inspection-meta-item">
                <span>Title</span>
                <strong>{declaredTitle}</strong>
              </div>

              <div className="inspection-meta-item">
                <span>Category</span>
                <strong>{declaredCategory}</strong>
              </div>

              <div className="inspection-meta-item">
                <span>Declared Condition</span>
                <strong>{declaredCondition}</strong>
              </div>

              <div className="inspection-meta-item">
                <span>Serial / IMEI / Identifier</span>
                <strong>{declaredSerial}</strong>
              </div>

              <div className="inspection-meta-item">
                <span>Declared Accessories</span>
                <strong>{declaredAccessories}</strong>
              </div>

              <div className="inspection-meta-item">
                <span>Seller Notes</span>
                <strong>{sellerDeclaredNotes}</strong>
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <h3 style={{ marginBottom: 8 }}>Description</h3>
              <p style={{ margin: 0, color: "#4b5b6a" }}>{declaredDescription}</p>
            </div>
          </section>

          {sellerPrepChecklist ? (
            <section className="inspection-card">
              <h2>Seller Prep Checklist Submitted</h2>
              <div className="inspection-checklist">
                {Object.entries(sellerPrepChecklist).map(([key, value]) => (
                  <div key={key} className="inspection-check-item inspection-check-item--readonly">
                    <input type="checkbox" checked={!!value} readOnly />
                    <span>
                      {key.replace(/([A-Z])/g, " $1").replace(/_/g, " ").trim()}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {sellerPrepPhotos.length ? (
            <section className="inspection-card">
              <h2>Seller Prep Photos</h2>
              <div className="inspection-photo-grid">
                {sellerPrepPhotos.map((url, idx) => (
                  <div key={`${url}-${idx}`} className="inspection-photo-card">
                    <img src={url} alt={`Seller prep ${idx + 1}`} />
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {listingPhotos.length ? (
            <section className="inspection-card">
              <h2>Listing Photos</h2>
              <div className="inspection-photo-grid">
                {listingPhotos.map((url, idx) => (
                  <div key={`${url}-${idx}`} className="inspection-photo-card">
                    <img src={url} alt={`Listing ${idx + 1}`} />
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section className="inspection-card">
            <h2>Inspector Verification Checklist</h2>
            <div className="inspection-checklist">
              <label className="inspection-check-item">
                <input
                  type="checkbox"
                  checked={form.serialMatch}
                  onChange={() => onToggle("serialMatch")}
                  disabled={submitting || !hasActiveSession}
                />
                <span>
                  Serial or identifying details match the declared reference
                  {declaredSerial !== "—" ? ` (${declaredSerial})` : ""}
                </span>
              </label>

              <label className="inspection-check-item">
                <input
                  type="checkbox"
                  checked={form.conditionMatch}
                  onChange={() => onToggle("conditionMatch")}
                  disabled={submitting || !hasActiveSession}
                />
                <span>
                  Actual item condition matches the declared condition
                  {declaredCondition !== "—" ? ` (${declaredCondition})` : ""}
                </span>
              </label>

              <label className="inspection-check-item">
                <input
                  type="checkbox"
                  checked={form.accessoriesIncluded}
                  onChange={() => onToggle("accessoriesIncluded")}
                  disabled={submitting || !hasActiveSession}
                />
                <span>
                  All declared accessories or components are present
                  {declaredAccessories !== "—" ? ` (${declaredAccessories})` : ""}
                </span>
              </label>

              <label className="inspection-check-item">
                <input
                  type="checkbox"
                  checked={form.powersOnOrBasicFunctionCheck}
                  onChange={() => onToggle("powersOnOrBasicFunctionCheck")}
                  disabled={submitting || !hasActiveSession}
                />
                <span>Item powers on or passes the required basic function check</span>
              </label>

              <label className="inspection-check-item">
                <input
                  type="checkbox"
                  checked={form.packagingAcceptable}
                  onChange={() => onToggle("packagingAcceptable")}
                  disabled={submitting || !hasActiveSession}
                />
                <span>Packaging is acceptable for safe intake, custody, or transport</span>
              </label>

              <label className="inspection-check-item">
                <input
                  type="checkbox"
                  checked={form.personalDataRemoved}
                  onChange={() => onToggle("personalDataRemoved")}
                  disabled={submitting || !hasActiveSession}
                />
                <span>Personal data appears removed or the item is intake-safe</span>
              </label>
            </div>

            <div className="inspection-result-box">
              <span
                className={`inspection-pill ${
                  form.pass ? "inspection-pill--pass" : "inspection-pill--fail"
                }`}
              >
                {form.pass ? "PASS" : "FAIL"}
              </span>
              <p>
                {form.pass
                  ? "All required verification checks currently pass."
                  : "One or more required verification checks failed, so this inspection will submit as failed."}
              </p>
            </div>
          </section>

          <section className="inspection-card">
            <h2>Inspector Notes</h2>
            <textarea
              className="inspection-textarea"
              value={form.notes}
              onChange={(e) => onText("notes", e.target.value)}
              placeholder="Record actual observed condition, missing accessories, defects, serial mismatches, packaging condition, and any operational notes."
              rows={7}
              disabled={submitting || !hasActiveSession}
            />
          </section>

          <section className="inspection-card">
            <div className="inspection-photos-header">
              <div>
                <h2>Actual Inspection Evidence Photos</h2>
                <p>{getPhotoRequirementText(inspectionMode)}</p>
              </div>

              <button
                type="button"
                className="inspection-btn inspection-btn--secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting || !hasActiveSession || photoFiles.length >= MAX_PHOTOS}
              >
                Add Photos
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              multiple
              style={{ display: "none" }}
              onChange={onPickPhotos}
            />

            <div className="inspection-photo-counter">
              {photoFiles.length} / {MAX_PHOTOS} photos selected
            </div>

            {photoPreviews.length ? (
              <div className="inspection-photo-grid">
                {photoPreviews.map((photo, idx) => (
                  <div key={photo.id} className="inspection-photo-card">
                    <img src={photo.url} alt={photo.name} />
                    <div className="inspection-photo-card__footer">
                      <span title={photo.name}>{photo.name}</span>
                      <button
                        type="button"
                        className="inspection-photo-remove"
                        onClick={() => removePhoto(idx)}
                        disabled={submitting}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="inspection-empty-photos">No inspection photos added yet.</div>
            )}

            {uploadProgressText ? (
              <div className="inspection-upload-progress">{uploadProgressText}</div>
            ) : null}
          </section>

          <section className="inspection-card">
            <div className="inspection-submit-row">
              <button
                type="submit"
                className="inspection-btn inspection-btn--primary"
                disabled={
                  submitting ||
                  !!successMsg ||
                  contextLoading ||
                  sessionLoading ||
                  !hasActiveSession ||
                  !isStageAllowed ||
                  !isPaymentReady ||
                  !isInspectionFeeReady
                }
              >
                {submitting
                  ? "Submitting..."
                  : !hasActiveSession
                  ? "Start Secure Session First"
                  : !isStageAllowed
                  ? "Inspection Not Allowed At Current Stage"
                  : !isPaymentReady || !isInspectionFeeReady
                  ? "Inspection Still Locked"
                  : "Submit Inspection"}
              </button>
            </div>
          </section>
        </form>
      </div>
    </div>
  );
}